import React, { useState, useEffect } from 'react';
import { sliderService } from '../../api/sliderService';
import { siteService } from '../../api/siteService';
import { uploadService } from '../../api/uploadService';
import '../Common/Common.css';

function SliderForm({ slider, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    site_id: '',
    title: '',
    media_type: 'image',
    image_url: '',
    mobile_image_url: '',
    video_url: '',
    link_url: '',
    is_active: true,
    position: 0,
  });

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({ desktop: false, mobile: false, video: false });
  const [sites, setSites] = useState([]);

  const [previews, setPreviews] = useState({ desktop: '', mobile: '', video: '' });

  useEffect(() => {
    siteService.getSites().then(setSites).catch(() => setSites([]));
  }, []);

  useEffect(() => {
    if (slider) {
      setFormData({
        site_id: slider.site_id || '',
        title: slider.title || '',
        media_type: slider.media_type || 'image',
        image_url: slider.image_url || '',
        mobile_image_url: slider.mobile_image_url || '',
        video_url: slider.video_url || '',
        link_url: slider.link_url || '',
        is_active: slider.is_active ?? true,
        position: slider.position || 0,
      });
      setPreviews({
        desktop: slider.image_url || '',
        mobile: slider.mobile_image_url || '',
        video: slider.video_url || '',
      });
    }
  }, [slider]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const uploadFile = async (file, fileType, field, previewKey) => {
    // Show local preview immediately
    if (fileType === 'image') {
      const reader = new FileReader();
      reader.onloadend = () => setPreviews((p) => ({ ...p, [previewKey]: reader.result }));
      reader.readAsDataURL(file);
    }

    setUploading((u) => ({ ...u, [previewKey]: true }));
    try {
      const result = await uploadService.uploadMedia(file, fileType, 'sliders');
      setFormData((prev) => ({ ...prev, [field]: result.url }));
      if (fileType === 'video') {
        setPreviews((p) => ({ ...p, video: result.url }));
      }
    } catch (err) {
      alert('Échec du téléchargement : ' + (err.response?.data?.error || err.message));
      setPreviews((p) => ({ ...p, [previewKey]: '' }));
    } finally {
      setUploading((u) => ({ ...u, [previewKey]: false }));
    }
  };

  const handleImageSelect = (e, field, previewKey) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Veuillez sélectionner une image'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Image trop grande (max 10 Mo)'); return; }
    uploadFile(file, 'image', field, previewKey);
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { alert('Veuillez sélectionner une vidéo'); return; }
    if (file.size > 100 * 1024 * 1024) { alert('Vidéo trop grande (max 100 Mo)'); return; }
    uploadFile(file, 'video', 'video_url', 'video');
  };

  const isAnyUploading = Object.values(uploading).some(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.media_type === 'image' && !formData.image_url) {
      alert('Veuillez télécharger une image desktop');
      return;
    }
    if (formData.media_type === 'video' && !formData.video_url) {
      alert('Veuillez télécharger une vidéo');
      return;
    }

    setLoading(true);
    try {
      const payload = { ...formData, site_id: formData.site_id || null };
      if (slider) {
        await sliderService.updateSlider(slider.id, payload);
      } else {
        await sliderService.createSlider(payload);
      }
      onSuccess();
    } catch (err) {
      console.error('Error saving slider:', err);
      alert('Échec de l\'enregistrement du slider');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      {/* Site assignment */}
      <div className="form-group">
        <label>Site</label>
        <select
          name="site_id"
          value={formData.site_id}
          onChange={handleChange}
          className="form-control"
        >
          <option value="">Tech360 (site principal)</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <small>Le slider n'apparaît que sur le site sélectionné</small>
      </div>

      {/* Title */}
      <div className="form-group">
        <label>Titre (Optionnel)</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="form-control"
          placeholder="Titre du slider"
        />
      </div>

      {/* Media type selector */}
      <div className="form-group">
        <label>Type de média *</label>
        <div style={{ display: 'flex', gap: '16px', marginTop: '6px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              name="media_type"
              value="image"
              checked={formData.media_type === 'image'}
              onChange={handleChange}
            />
            Image
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              name="media_type"
              value="video"
              checked={formData.media_type === 'video'}
              onChange={handleChange}
            />
            Vidéo
          </label>
        </div>
      </div>

      {/* IMAGE MODE */}
      {formData.media_type === 'image' && (
        <>
          {/* Desktop image */}
          <div className="form-group">
            <label>Image Desktop *</label>
            <small style={{ display: 'block', color: '#718096', marginBottom: '8px' }}>
              Recommandé : 1920×600 px (paysage)
            </small>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageSelect(e, 'image_url', 'desktop')}
              className="form-control"
              disabled={uploading.desktop}
            />
            {uploading.desktop && <div style={{ color: '#dc143c', fontSize: '14px', marginTop: '6px' }}>Téléchargement…</div>}
            {previews.desktop && (
              <img
                src={previews.desktop}
                alt="Desktop preview"
                style={{ marginTop: '10px', maxWidth: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: '6px' }}
              />
            )}
          </div>

          {/* Mobile image (optional) */}
          <div className="form-group">
            <label>Image Mobile (Optionnelle)</label>
            <small style={{ display: 'block', color: '#718096', marginBottom: '8px' }}>
              Recommandé : 768×500 px (portrait/carré). Si absente, l'image desktop sera utilisée.
            </small>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageSelect(e, 'mobile_image_url', 'mobile')}
              className="form-control"
              disabled={uploading.mobile}
            />
            {uploading.mobile && <div style={{ color: '#dc143c', fontSize: '14px', marginTop: '6px' }}>Téléchargement…</div>}
            {previews.mobile && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '10px' }}>
                <img
                  src={previews.mobile}
                  alt="Mobile preview"
                  style={{ maxWidth: '120px', maxHeight: '160px', objectFit: 'contain', borderRadius: '6px' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setFormData((p) => ({ ...p, mobile_image_url: '' }));
                    setPreviews((p) => ({ ...p, mobile: '' }));
                  }}
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '4px 10px' }}
                >
                  Supprimer
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* VIDEO MODE */}
      {formData.media_type === 'video' && (
        <div className="form-group">
          <label>Vidéo *</label>
          <small style={{ display: 'block', color: '#718096', marginBottom: '8px' }}>
            Formats acceptés : MP4, WebM. Max 100 Mo. La vidéo sera lue en boucle, sans son.
          </small>
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoSelect}
            className="form-control"
            disabled={uploading.video}
          />
          {uploading.video && <div style={{ color: '#dc143c', fontSize: '14px', marginTop: '6px' }}>Téléchargement…</div>}
          {previews.video && (
            <video
              src={previews.video}
              controls
              muted
              style={{ marginTop: '10px', maxWidth: '100%', maxHeight: '180px', borderRadius: '6px' }}
            />
          )}
        </div>
      )}

      {/* Link URL */}
      <div className="form-group">
        <label>URL du lien (Optionnel)</label>
        <input
          type="url"
          name="link_url"
          value={formData.link_url}
          onChange={handleChange}
          className="form-control"
          placeholder="https://example.com/product"
        />
        <small>Où les utilisateurs vont lorsqu'ils cliquent sur le slider</small>
      </div>

      {/* Position */}
      <div className="form-group">
        <label>Position</label>
        <input
          type="number"
          name="position"
          value={formData.position}
          onChange={handleChange}
          className="form-control"
          min="0"
          required
        />
        <small>Les numéros plus petits apparaissent en premier</small>
      </div>

      {/* Active */}
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <span>Actif</span>
        </label>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" disabled={loading || isAnyUploading} className="btn btn-primary">
          {loading ? 'Enregistrement…' : isAnyUploading ? 'Téléchargement…' : slider ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
}

export default SliderForm;
